import { Input } from '@/components/ui/input'
import type { ConditionValue, OperatorDef, PropertyDef } from '../query/types'
import { OptionChecklist } from './OptionChecklist'
import { RefPicker } from './RefPicker'
import type { FilterOptions } from './useFilterOptions'

const INPUT = 'max-md:h-11 max-md:text-base'

/** The two ends of a range as the inputs show them; an end that is not filled in yet is ''. */
function ends(value: ConditionValue | undefined): [string, string] {
  const [from, to] = Array.isArray(value) ? value : []
  return [from === undefined ? '' : String(from), to === undefined ? '' : String(to)]
}

/** A number typed in a number input; null for an empty field (or one the browser could not read). */
function numberOf(text: string): number | null {
  if (text.trim() === '') return null
  const number = Number(text)
  return Number.isFinite(number) ? number : null
}

function Range({
  type,
  value,
  onChange,
}: {
  type: 'date' | 'number'
  value: ConditionValue | undefined
  onChange: (value: ConditionValue | undefined) => void
}) {
  const [from, to] = ends(value)
  // A range with one end filled in stays in the draft as it is; it only counts once both are good.
  const write = (nextFrom: string, nextTo: string) => {
    if (nextFrom === '' && nextTo === '') {
      onChange(undefined)
    } else if (type === 'date') {
      onChange([nextFrom, nextTo])
    } else {
      onChange([numberOf(nextFrom) ?? '', numberOf(nextTo) ?? ''])
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input type={type} aria-label="Van" value={from} onChange={(event) => write(event.target.value, to)} className={`w-auto min-w-0 flex-1 ${INPUT}`} />
      <span className="text-sm text-muted-foreground">t/m</span>
      <Input type={type} aria-label="Tot en met" value={to} onChange={(event) => write(from, event.target.value)} className={`w-auto min-w-0 flex-1 ${INPUT}`} />
    </div>
  )
}

/**
 * The value of one condition (design §5.3), whatever its operator takes: nothing, a text, a choice
 * from a list (or organisations and persons through a search), a date or a range of dates, a number
 * of days, a number or a range of numbers. The value is handed over as typed: whether it is good
 * enough to count is for `completeCondition`.
 */
export function ValueEditor({
  property,
  operator,
  value,
  options,
  onChange,
}: {
  property: PropertyDef
  operator: OperatorDef
  value: ConditionValue | undefined
  options: FilterOptions
  onChange: (value: ConditionValue | undefined) => void
}) {
  switch (operator.value) {
    case 'none':
      return null
    case 'text':
      return (
        <Input
          aria-label="Waarde"
          maxLength={200}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value || undefined)}
          className={INPUT}
        />
      )
    case 'list': {
      const ids = Array.isArray(value) ? value.map(String) : []
      const change = (next: string[]) => onChange(next.length > 0 ? next : undefined)
      const source = property.options?.source
      if (source === 'organisations' || source === 'persons') {
        return <RefPicker kind={source} label={property.label} value={ids} options={options} onChange={change} />
      }
      return (
        <OptionChecklist
          label={property.label}
          options={options.optionsFor(property)}
          value={ids}
          onChange={change}
          displayLabel={property.key === 'owner' ? (option) => (option.value === 'none' ? 'Geen eigenaar' : option.label) : undefined}
        />
      )
    }
    case 'date':
      return (
        <Input
          type="date"
          aria-label="Datum"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value || undefined)}
          className={INPUT}
        />
      )
    case 'date_range':
      return <Range type="date" value={value} onChange={onChange} />
    case 'days':
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={3650}
            aria-label="Aantal dagen"
            value={typeof value === 'number' ? String(value) : ''}
            onChange={(event) => onChange(numberOf(event.target.value) ?? undefined)}
            className={`w-24 ${INPUT}`}
          />
          <span className="text-sm text-muted-foreground">dagen</span>
        </div>
      )
    case 'number':
      return (
        <Input
          type="number"
          aria-label="Waarde"
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(event) => onChange(numberOf(event.target.value) ?? undefined)}
          className={`w-32 ${INPUT}`}
        />
      )
    case 'number_range':
      return <Range type="number" value={value} onChange={onChange} />
  }
}
